import { Field, Input, type InputProps } from '@chakra-ui/react';
import type React from 'react';

import { isValidDateString } from '~/utils/date-utils';

interface DateInputProps extends Omit<InputProps, 'onChange'> {
  label?: string;
  value: string; // Expected format: YYYY-MM-DD
  onChange: (value: string) => void;
  isRequired?: boolean;
  size?: 'sm' | 'md' | 'lg';
  min?: string; // YYYY-MM-DD format
  max?: string; // YYYY-MM-DD format
}

export const DateInput: React.FC<DateInputProps> = ({
  label,
  value,
  onChange,
  isRequired = false,
  size = 'md',
  min,
  max,
  ...props
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // HTML date input returns YYYY-MM-DD format
    // Store it exactly as returned without any conversion
    onChange(e.target.value);
  };

  // Use centralized date validation
  const isValidDate = (dateString: string) => {
    // Allow empty values for non-required fields
    if (!dateString && !isRequired) return true;

    // Use the centralized validation from date-utils
    return isValidDateString(dateString);
  };

  const getErrorMessage = () => {
    if (!value && isRequired) return 'This field is required';
    if (value && !isValidDate(value)) return 'Please enter a valid date';
    return '';
  };

  const inputElement = (
    <Input
      type="date"
      value={value}
      onChange={handleChange}
      size={size}
      min={min}
      max={max}
      {...props}
    />
  );

  // If no label, return just the input
  if (!label) {
    return inputElement;
  }

  return (
    <Field.Root>
      <Field.Label>
        {label}
        {isRequired && <Field.RequiredIndicator />}
      </Field.Label>
      {inputElement}
      {getErrorMessage() && <Field.ErrorText>{getErrorMessage()}</Field.ErrorText>}
    </Field.Root>
  );
};
